import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookingModal } from '../components/shared/BookingModal';
import { MapPin, Globe, Sparkles, Search, Code, ArrowRight, CheckCircle } from 'lucide-react';

const services = [
    {
        slug: 'gmb-optimization-services',
        icon: MapPin,
        name: 'GMB / GBP Optimization',
        tagline: 'Dominate Google Maps',
        description: 'Complete Google Business Profile optimization to rank #1 in local searches and Maps. Perfect for businesses with physical locations wanting to capture local customers.',
        features: [
            'Complete profile optimization',
            'Review generation & management',
            'Local citation building',
            'Photo & video optimization',
            'Weekly post scheduling',
            'Q&A management',
            'Competitor analysis',
            'Monthly ranking reports',
        ],
        idealFor: ['Clinics', 'Restaurants', 'Retail Stores', 'Service Businesses'],
        color: 'from-red-500/20 to-orange-500/20',
    },
    {
        slug: 'geo-seo-services',
        icon: Globe,
        name: 'GEO SEO',
        tagline: 'Location-Based Dominance',
        description: 'Rank for city + service keywords to capture high-intent local traffic. Ideal for businesses operating in multiple locations or targeting specific geographic areas.',
        features: [
            'City-specific landing pages',
            'Local keyword research',
            'NAP consistency audit',
            'Local link building',
            'Location-based content',
            'Schema markup implementation',
            'Multi-location management',
            'Local competitor tracking',
        ],
        idealFor: ['Multi-location businesses', 'Service area businesses', 'Franchises'],
        color: 'from-green-500/20 to-emerald-500/20',
    },
    {
        slug: 'ai-search-optimization',
        icon: Sparkles,
        name: 'AI Search Optimization',
        tagline: 'Future-Proof Visibility',
        description: 'Get featured in ChatGPT, Google SGE, Perplexity, and other AI search platforms. Stay ahead as search evolves beyond traditional results.',
        features: [
            'AI content optimization',
            'Entity & knowledge building',
            'Structured data enhancement',
            'Authority signal building',
            'Conversational query optimization',
            'AI citation tracking',
            'Brand mention monitoring',
            'AI search reports',
        ],
        idealFor: ['Tech companies', 'SaaS businesses', 'Thought leaders', 'Early adopters'],
        color: 'from-purple-500/20 to-pink-500/20',
    },
    {
        slug: 'seo-services',
        icon: Search,
        name: 'High-Intent SEO',
        tagline: 'Revenue-Driven Rankings',
        description: 'Target commercial keywords that drive conversions, not just traffic. Focus on bottom-of-funnel keywords that bring ready-to-buy customers.',
        features: [
            'Commercial keyword research',
            'On-page optimization',
            'Technical SEO audit',
            'Content strategy',
            'Link building campaigns',
            'Conversion optimization',
            'Competitor gap analysis',
            'ROI tracking & reporting',
        ],
        idealFor: ['E-commerce', 'Lead generation', 'B2B companies', 'Service businesses'],
        color: 'from-blue-500/20 to-cyan-500/20',
    },
    {
        slug: 'programmatic-seo',
        icon: Code,
        name: 'Programmatic SEO',
        tagline: 'Scale to 1000+ Pages',
        description: 'Generate hundreds of SEO-optimized pages automatically to capture long-tail traffic. Perfect for businesses with large inventories or service areas.',
        features: [
            'Template creation & design',
            'Data integration setup',
            'Auto-generation system',
            'Internal linking automation',
            'Quality control processes',
            'Performance tracking',
            'Indexation management',
            'Scaling roadmap',
        ],
        idealFor: ['Marketplaces', 'Directories', 'SaaS platforms', 'Large catalogs'],
        color: 'from-amber-500/20 to-yellow-500/20',
    },
];

export default function ServicesPage() {
    const [bookingOpen, setBookingOpen] = useState(false);

    return (
        <div className="relative" data-testid="services-page">
            {/* Hero */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-4xl">
                        <p className="text-sm font-medium tracking-wider uppercase text-primary mb-4">
                            Our Services
                        </p>
                        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
                            Search Visibility Solutions
                        </h1>
                        <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                            From traditional SEO to AI-powered search, we offer comprehensive solutions 
                            to make your business visible wherever your customers are looking.
                        </p>
                    </div>
                </div>
            </section>

            {/* Services List */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="space-y-16">
                        {services.map((service, index) => (
                            <div 
                                key={index}
                                id={service.slug}
                                className="scroll-mt-24"
                                data-testid={`service-detail-${index}`}
                            >
                                <div className={`rounded-3xl bg-gradient-to-br ${service.color} p-px`}>
                                    <div className="rounded-3xl bg-background p-8 md:p-12">
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                                            <div>
                                                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                                                    <service.icon className="w-7 h-7 text-primary" />
                                                </div>
                                                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">
                                                    {service.name}
                                                </h2>
                                                <p className="text-lg text-primary font-medium mb-4">
                                                    {service.tagline}
                                                </p>
                                                <p className="text-muted-foreground leading-relaxed mb-8">
                                                    {service.description}
                                                </p>

                                                <div className="mb-8">
                                                    <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-4">
                                                        Ideal For
                                                    </h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {service.idealFor.map((item, i) => (
                                                            <span 
                                                                key={i}
                                                                className="px-3 py-1 rounded-full text-sm bg-muted text-muted-foreground"
                                                            >
                                                                {item}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="flex flex-col sm:flex-row gap-4">
                                                    <Button 
                                                        className="btn-glow rounded-full"
                                                        onClick={() => setBookingOpen(true)}
                                                    >
                                                        Get Started
                                                        <ArrowRight className="w-4 h-4 ml-2" />
                                                    </Button>
                                                    <Button 
                                                        variant="outline"
                                                        asChild
                                                        className="rounded-full"
                                                    >
                                                        <Link to={`/${service.slug}`}>
                                                            Learn More
                                                        </Link>
                                                    </Button>
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-6">
                                                    What's Included
                                                </h4>
                                                <div className="grid grid-cols-1 gap-3">
                                                    {service.features.map((feature, i) => (
                                                        <div 
                                                            key={i}
                                                            className="flex items-center gap-3 p-3 rounded-xl bg-muted/50"
                                                        >
                                                            <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                                                            <span>{feature}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-padding bg-foreground text-background">
                <div className="container-custom text-center">
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                        Not Sure Which Service You Need?
                    </h2>
                    <p className="text-lg text-background/70 max-w-2xl mx-auto mb-8">
                        Book a free strategy call and we'll analyze your business to recommend 
                        the perfect combination of services.
                    </p>
                    <Button 
                        size="lg"
                        className="bg-background text-foreground hover:bg-background/90 rounded-full px-8"
                        onClick={() => setBookingOpen(true)}
                        data-testid="services-cta-btn"
                    >
                        Book Free Consultation
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </section>

            <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
        </div>
    );
}
